// Message types
export type Message = {
  id: string;
  role: "user" | "bot" | "system";
  content: string;
  timestamp: Date;
};

// Intake data structure
export type IntakeData = {
  title?: string;
  problem_statement?: string;
  desired_outcome?: string;
  business_impact?: string;
  annualized_entitlement?: string;
  impact_metrics?: string;
  stakeholders?: string;
  urgency?: string;
  program_name?: string;
  theme?: string;
  assigned_team?: string;
  submitted_by?: string;
  date_submitted?: string;
  conversation_summary?: string;
  target_year?: string;
  request_type?: string;
};

// Workflow stages
export enum Stage {
  RequestType = "requesttype",
  Intake = "intake",
  StructuredCapture = "structuredcapture",
  Summarization = "summarization",
  Complete = "complete",
}

// Prioritization result from AI assessment
export type PrioritizationAssessment = {
  /** Whether the request meets acceptance criteria */
  meetsAcceptanceCriteria: boolean;
  /** The OP decision category: P1, P2, ATL, BTL */
  op_decision: string;
  /** Mission fit rating */
  mission_fit: string;
  /** AI-generated rationale explaining why */
  rationale: string;
};

// Similar requests response type
export type SimilarRequestsResponse = {
  count: number;
  similar_requests: any[];
  query_info: {
    program_filter: string;
  };
};

// Main workflow state
export type WorkflowState = {
  // Conversation
  messages: Message[];
  stage: Stage;
  loading: boolean;
  
  // Data collection
  intakeData: IntakeData;
  finalJSON: any | null;
  
  // Session
  sessionId: string;
  selectedRequestType: 'op' | 'urgent' | null;
  
  // Results
  asanaTaskUrl: string | null;
  similarRequests: SimilarRequestsResponse | null;
  
  // Error handling
  submissionError: ErrorState | null;
  
  // UI state
  sidebarOpen: boolean;
  similarRequestsLoading: boolean;
  
  // Edit mode tracking (human-in-the-loop)
  isEditMode: boolean;
  editModeStartTime: Date | null;
  showManualSummaryOption: boolean;
  lastCompletionAttempt: Date | null;
  
  // Prioritization assessment (pre-submit)
  prioritizationResult: PrioritizationAssessment | null;
  prioritizationLoading: boolean;
  prioritizationDismissed: boolean;
};

// Workflow actions
export type WorkflowAction =
  | { type: 'ADD_USER_MESSAGE'; payload: string }
  | { type: 'ADD_BOT_MESSAGE'; payload: string }
  | { type: 'ADD_SYSTEM_MESSAGE'; payload: string }
  | { type: 'SET_STAGE'; payload: Stage }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'UPDATE_INTAKE_DATA'; payload: Partial<IntakeData> }
  | { type: 'SET_FINAL_JSON'; payload: any }
  | { type: 'SET_REQUEST_TYPE'; payload: 'op' | 'urgent' }
  | { type: 'COMPLETE_SUBMISSION'; payload: { taskUrl: string } }
  | { type: 'SUBMISSION_ERROR'; payload: { error: string; retryable: boolean } }
  | { type: 'SET_SIMILAR_REQUESTS'; payload: SimilarRequestsResponse }
  | { type: 'SET_SIMILAR_REQUESTS_LOADING'; payload: boolean }
  | { type: 'TOGGLE_SIDEBAR' }
  | { type: 'SET_SIDEBAR_OPEN'; payload: boolean }
  | { type: 'RESET'; payload: { userEmail: string } }
  | { type: 'ENTER_EDIT_MODE' }
  | { type: 'EXIT_EDIT_MODE' }
  | { type: 'SHOW_MANUAL_SUMMARY_OPTION'; payload: boolean }
  | { type: 'SET_LAST_COMPLETION_ATTEMPT' }
  | { type: 'SET_PRIORITIZATION_RESULT'; payload: PrioritizationAssessment }
  | { type: 'SET_PRIORITIZATION_LOADING'; payload: boolean }
  | { type: 'DISMISS_PRIORITIZATION' };

// IRIS service types (enhanced)
export type IRISRequest = {
  userMessage: string;
  stage: 'intake' | 'summarization' | 'finaljson' | 'explore';
  agentId: string;
  intakeData?: IntakeData;
  sessionId?: string;
  // Explore mode specific fields
  explorePhase?: number;
  explorePhaseData?: any;
  phaseName?: string;
};

export type IRISResponse = {
  // The conversational reply from IRIS
  reply: string;
  
  // Structured data extracted from the conversation
  extractedData?: Partial<IntakeData>;
  
  // Indicates if the intake process is complete
  isComplete?: boolean;
  
  // Confidence scores for extracted data (0-1)
  confidence?: {
    [key: string]: number;
  };
  
  // Any errors or warnings
  error?: string;
  warnings?: string[];
  
  // Metadata about the processing
  metadata?: {
    processingTime?: number;
    modelUsed?: string;
    tokensUsed?: number;
  };
};

// Error handling types
export type ErrorState = {
  message: string;
  code?: string;
  timestamp: Date;
  retryable: boolean;
  context?: any;
};

// Loading state types for better UX
export type LoadingContext = {
  operation: string;
  progress?: number;
  estimatedTime?: number;
  canCancel?: boolean;
};
